import { Autocomplete, Box, Button, Snackbar, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useGetEmailTemplateRecipients } from '../../../../services/react-query/email_templates/useGetEmailTemplateRecipients';
import { useSendEmails } from '../../../../services/react-query/email_templates/useSendEmails';
import { useIsMutating } from '@tanstack/react-query';
import { mutationKeys } from '../../../../services/react-query/mutationKeys';
import { Contact } from '../../../../types/Contact';
import { EmailTemplateRecipientsType } from '../../../../types/EmailTemplate';
import EmployeeSearchAutocomplete, { EmployeeOption } from '../../../common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import { editPreviewModalStyles as styles } from './editPreviewModalStyles';
import _ from 'lodash';
import { DisableContext } from '../../../../contexts/DisableContextProvider';

interface Props {
  eventEmailTemplateUuid: string;
  eventContacts: Contact[];
}

export default function EmailSendForm({ eventEmailTemplateUuid, eventContacts }: Props) {
  const { disabled } = useContext(DisableContext);

  const [recipientType, setRecipientType] = useState(EmailTemplateRecipientsType.CONTACT);
  const [recipients, setRecipients] = useState<(Contact | EmployeeOption)[]>([]);
  const [sent, setSent] = useState(false);
  const { data: defaultContactRecipientsResponse } = useGetEmailTemplateRecipients({
    eventEmailTemplateUuid,
  });
  const defaultContactRecipients = defaultContactRecipientsResponse?.data;

  useEffect(() => {
    if (recipientType === EmailTemplateRecipientsType.CONTACT && defaultContactRecipients) {
      setRecipients(defaultContactRecipients);
    } else {
      setRecipients([]);
    }
  }, [recipientType, defaultContactRecipients]);

  const { mutate: sendEmails } = useSendEmails({
    onSuccessCallback: () => setSent(true),
  });
  const isSendEmailsMutating = useIsMutating({
    mutationKey: [mutationKeys.sendEmails],
  });

  const handleContactChange = (newRecipients: Contact[]) => {
    setRecipients(newRecipients);
  };

  const handleEmployeeChange = (newRecipients: EmployeeOption[]) => {
    setRecipients(newRecipients);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newType = newValue === 0 ? EmailTemplateRecipientsType.CONTACT : EmailTemplateRecipientsType.EMPLOYEE;
    setRecipientType(newType);
    setRecipients([]);
  };

  const handleSend = () => {
    sendEmails({
      eventEmailTemplateUuid,
      recipientType,
      recipientUuids: _.map(recipients, 'uuid'),
    });
  };

  const isSendButtonDisabled = disabled || recipients.length === 0 || Boolean(isSendEmailsMutating);

  return (
    <Box sx={styles.sendFormContainer}>
      <Box sx={styles.sendFormTitle}>
        <Typography variant="h6">Select Recipients</Typography>
        <Button variant="contained" onClick={handleSend} disabled={isSendButtonDisabled}>
          Send
        </Button>
      </Box>

      <Box sx={styles.sendFormTabsWrapper}>
        <Tabs value={recipientType === EmailTemplateRecipientsType.CONTACT ? 0 : 1} onChange={handleTabChange}>
          <Tab label="Contacts" />
          <Tab label="Employees" />
        </Tabs>
      </Box>

      {recipientType === EmailTemplateRecipientsType.CONTACT ? (
        <Autocomplete
          multiple
          options={eventContacts}
          value={recipients as Contact[]}
          getOptionLabel={(c) => `${c.full_name ?? `${c.first_name} ${c.last_name}`} (${c.email})`}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          onChange={(_, val) => handleContactChange(val)}
          renderInput={(params) => <TextField {...params} label="Contacts" />}
        />
      ) : (
        <EmployeeSearchAutocomplete<true> value={recipients as EmployeeOption[]} onChange={handleEmployeeChange} isMultiple label="Employees" />
      )}
      <Snackbar open={sent} onClose={() => setSent(false)} message="sent!" />
    </Box>
  );
}
